/* Шорткат iOS: личный ключ участника и установка шортката на телефон.

   Шорткат спрашивает «текстом или голосом», отправляет сказанное на сервер и
   показывает уведомлением, что записалось. Дальше его вешают на касание задней
   панели, кнопку «Действие» или экран блокировки — это уже настройки iPhone.

   Ссылка установки — shortcuts://import-shortcut: по ней сразу открывается
   приложение «Команды», без промежуточной страницы в браузере. */

import { Button, List, ListButton, ListItem } from "konsta/react";
import React, { useState } from "react";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelShortcut } from "../components/skeleton.jsx";
import { ErrorState, Hint } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useFetch } from "../lib/store.jsx";
import { alertDialog, confirmDialog, copyText, haptic, openLink } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const TILE = { install: "#af52de", key: "#30b0c7" };

/* Ключ длинный — в строке показываем начало и хвост, целиком он уходит в буфер */
const shortKey = (key) => `${key.slice(0, 9)}…${key.slice(-4)}`;

export default function Shortcut() {
  const { data, loading, error, refetch, setData } = useFetch("/shortcut");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      setData(await api.post("/shortcut/key"));
      haptic.success();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    const ok = await confirmDialog("Старый ключ перестанет работать — шорткат на телефоне придётся поставить заново. Выпустить новый?");
    if (!ok) return;
    await create();
  };

  const copy = async () => {
    haptic.light();
    alertDialog((await copyText(data.key)) ? "Ключ скопирован" : data.key);
  };

  const install = () => {
    haptic.medium();
    openLink(data.install);
  };

  if (loading) return <Screen back title="Шорткат"><SkelShortcut /></Screen>;
  if (error) return <Screen back title="Шорткат"><ErrorState text={error} onRetry={refetch} /></Screen>;

  return (
    <Screen back title="Шорткат" bottomPad={40}>
      <div className="px-4 pt-1 pb-1">
        <div className="card-tg px-4 py-3.5">
          <div className="flex items-center gap-2.5">
            <Ion.flash size={20} style={{ color: TILE.install }} />
            <span className="display text-[16px] font-semibold">Запись без Telegram</span>
          </div>
          <div className="mt-2 text-[14px] leading-snug">
            Шорткат спрашивает «текстом или голосом», отправляет сказанное сюда и показывает
            уведомлением, что записалось. Расход, доход, задача, вопрос по данным — как в боте,
            только без подтверждения: ошибку правят в приложении.
          </div>
        </div>
      </div>

      {!data.key ? (
        <>
          <div className="px-4 pt-3">
            <Button large rounded onClick={create} disabled={busy}>
              Создать шорткат
            </Button>
          </div>
          <div className="px-4 pt-3">
            <Hint>Ключ выпускается на вас — по нему шорткат пишет от вашего имени.</Hint>
          </div>
        </>
      ) : (
        <>
          <List strong inset>
            <ListItem
              link
              colors={ITEM_COLORS}
              media={<IconTile icon={Ion.flashSolid} bg={TILE.install} />}
              title="Установить на iPhone"
              onClick={install}
            />
            <ListItem
              link
              chevron={false}
              colors={ITEM_COLORS}
              media={<IconTile icon={Ion.keySolid} bg={TILE.key} />}
              title="Ключ"
              after={<span className="num">{shortKey(data.key)}</span>}
              onClick={copy}
            />
          </List>

          <div className="px-4 pb-1">
            {data.manual ? (
              <Hint>
                Нажмите на айфоне — «Команды» предложат добавить шорткат. После установки откройте
                его на правку и вставьте свой ключ в первое поле «Текст»: ссылка общая, ключ у
                каждого свой. Нажатие на строку «Ключ» копирует его в буфер.
              </Hint>
            ) : (
              <Hint>
                Нажмите на айфоне — откроются «Команды» и предложат добавить шорткат с уже зашитым
                ключом. Если iOS откажется, включите Настройки → Быстрые команды → «Разрешить
                ненадёжные команды»: пункт появляется после первого запуска любого шортката.
              </Hint>
            )}
          </div>

          <div className="px-4 pt-3">
            <Hint>
              Куда повесить: двойное касание по крышке — Настройки → Универсальный доступ → Касание →
              Касание задней панели; кнопка «Действие» — Настройки → Кнопка «Действие»; экран
              блокировки — долгое нажатие на экран → «Настроить» → виджет «Быстрые команды».
            </Hint>
          </div>

          {!data.public ? (
            <div className="px-4 pt-3">
              <Hint>
                Адрес сервера сейчас локальный — с телефона шорткат до него не достучится.
                Нужен публичный https: пропишите MINIAPP_URL или PUBLIC_API_URL.
              </Hint>
            </div>
          ) : null}

          {!data.llm ? (
            <div className="px-4 pt-3">
              <Hint>ИИ-разбор не настроен (LLM_API_KEY) — шорткат установится, но записывать будет нечего.</Hint>
            </div>
          ) : null}

          <List strong inset>
            <ListButton colors={{ textIos: "text-[color:var(--fin-red)]" }} onClick={rotate}>
              Выпустить ключ заново
            </ListButton>
          </List>
        </>
      )}
    </Screen>
  );
}
